import type { RGB, TemplateResult, TemplateSettings } from "./types";
import { effectiveCellPx } from "./types";
import { contrastColor, symbolFor } from "./symbols";
import { buildTemplate } from "./quantize";

const PAD_COLOR = "#ffffff";
const LINE_COLOR = "#000000";
const NUMBER_COLOR = "#282828";
const LEGEND_SWATCH = 36;
const LEGEND_GAP = 12;
const LEGEND_ROWS_MAX = 2;
const LEGEND_COL_W = 200; // wider than Python to fit "Symbol  #RRGGBB  · 999 cells"
const MARGIN_TOP = 48;
const MARGIN_LEFT = 48;
const MARGIN_RIGHT = 24;
const MARGIN_BELOW_GRID = 24;
const MINOR_LINE_W = 1;
const MAJOR_LINE_W = 3;

/**
 * Decode an uploaded file into an ImageBitmap that respects EXIF orientation.
 * iPhone JPEGs in particular need this; otherwise portrait photos render rotated.
 */
export async function decodeImageFile(file: File): Promise<ImageBitmap> {
  return await createImageBitmap(file, { imageOrientation: "from-image" });
}

/**
 * Pad the bitmap to a square, white background, then downsample to GRID×GRID
 * using the browser's high-quality smoothing (≈ Lanczos).
 *
 * Returns a 25×25 ImageData ready for quantization.
 */
export function rasterizeToGrid(bitmap: ImageBitmap, grid: number): ImageData {
  const side = Math.max(bitmap.width, bitmap.height);

  // Square-pad onto an off-DOM canvas. Regular HTMLCanvasElement, not OffscreenCanvas,
  // for max browser support.
  const padCanvas = document.createElement("canvas");
  padCanvas.width = side;
  padCanvas.height = side;
  const padCtx = padCanvas.getContext("2d", { willReadFrequently: false });
  if (!padCtx) throw new Error("Could not acquire 2D context for square-pad canvas");
  padCtx.fillStyle = PAD_COLOR;
  padCtx.fillRect(0, 0, side, side);
  padCtx.drawImage(
    bitmap,
    Math.floor((side - bitmap.width) / 2),
    Math.floor((side - bitmap.height) / 2)
  );

  // Downsample.
  const small = document.createElement("canvas");
  small.width = grid;
  small.height = grid;
  const smallCtx = small.getContext("2d", { willReadFrequently: true });
  if (!smallCtx) throw new Error("Could not acquire 2D context for downsample canvas");
  smallCtx.imageSmoothingEnabled = true;
  smallCtx.imageSmoothingQuality = "high";
  smallCtx.drawImage(padCanvas, 0, 0, grid, grid);
  return smallCtx.getImageData(0, 0, grid, grid);
}

export interface RenderLayout {
  width: number;
  height: number;
  gridX: number;
  gridY: number;
  cellPx: number;
  legendY: number;
  legendCols: number;
  legendRows: number;
}

export function computeLayout(result: TemplateResult, s: TemplateSettings): RenderLayout {
  const cellPx = effectiveCellPx(s);
  const gridSide = result.grid * cellPx;
  const legendCount = result.palette.length;
  // Spread the legend across more rows when there are lots of colors (cross-stitch).
  const legendRowsMax = legendCount > 12 ? 4 : LEGEND_ROWS_MAX;
  const legendRows = Math.min(legendRowsMax, legendCount);
  const legendCols = Math.ceil(legendCount / legendRows);
  const legendW = legendCols * LEGEND_COL_W + 20;
  const legendH = legendRows * (LEGEND_SWATCH + LEGEND_GAP) + 10;

  const width = Math.max(MARGIN_LEFT + gridSide + MARGIN_RIGHT, MARGIN_LEFT + legendW + MARGIN_RIGHT);
  const height = MARGIN_TOP + gridSide + MARGIN_BELOW_GRID + legendH + 20;

  return {
    width,
    height,
    gridX: MARGIN_LEFT,
    gridY: MARGIN_TOP,
    cellPx,
    legendY: MARGIN_TOP + gridSide + MARGIN_BELOW_GRID,
    legendCols,
    legendRows,
  };
}

function fmtHex([r, g, b]: RGB): string {
  const h = (n: number) => n.toString(16).padStart(2, "0").toUpperCase();
  return `#${h(r)}${h(g)}${h(b)}`;
}

/**
 * Render the full template (cells + grid + numbers + legend) onto a canvas.
 * Pass `progress` to draw "done" check overlays on those cell indices.
 */
export function renderTemplate(
  canvas: HTMLCanvasElement,
  result: TemplateResult,
  settings: TemplateSettings,
  progress?: ReadonlySet<number>
): void {
  const layout = computeLayout(result, settings);
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not acquire 2D context for render canvas");

  // Background.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, layout.width, layout.height);

  // Cells.
  const { grid, indices, palette } = result;
  const { gridX, gridY, cellPx } = layout;
  for (let row = 0; row < grid; row++) {
    for (let col = 0; col < grid; col++) {
      const idx = indices[row * grid + col];
      const [r, g, b] = palette[idx];
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(gridX + col * cellPx, gridY + row * cellPx, cellPx, cellPx);
    }
  }

  // Symbol overlays (skip when cells are tiny — they'd be unreadable anyway).
  if (settings.showSymbols && cellPx >= 12) {
    ctx.font = `bold ${Math.floor(cellPx * 0.55)}px "Segoe UI Symbol","Apple Symbols","Noto Sans Symbols",sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (let row = 0; row < grid; row++) {
      for (let col = 0; col < grid; col++) {
        const idx = indices[row * grid + col];
        const [r, g, b] = palette[idx];
        ctx.fillStyle = contrastColor(r, g, b);
        ctx.fillText(
          symbolFor(idx),
          gridX + col * cellPx + cellPx / 2,
          gridY + row * cellPx + cellPx / 2 + 1
        );
      }
    }
  }

  // Progress check overlays (drawn on top of symbols).
  if (progress && progress.size > 0) {
    ctx.font = `bold ${Math.floor(cellPx * 0.7)}px "Segoe UI Symbol",sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const cellIdx of progress) {
      const row = Math.floor(cellIdx / grid);
      const col = cellIdx % grid;
      const [r, g, b] = palette[indices[cellIdx]];
      // semi-transparent dim
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.fillRect(gridX + col * cellPx, gridY + row * cellPx, cellPx, cellPx);
      ctx.fillStyle = contrastColor(r, g, b);
      ctx.fillText(
        "✓",
        gridX + col * cellPx + cellPx / 2,
        gridY + row * cellPx + cellPx / 2
      );
    }
  }

  // Gridlines (minor first, then major on top).
  ctx.strokeStyle = LINE_COLOR;
  for (let i = 0; i <= grid; i++) {
    const major = i % settings.majorLineEvery === 0;
    ctx.lineWidth = major ? MAJOR_LINE_W : MINOR_LINE_W;
    ctx.beginPath();
    const x = gridX + i * cellPx;
    const y = gridY + i * cellPx;
    ctx.moveTo(x, gridY);
    ctx.lineTo(x, gridY + grid * cellPx);
    ctx.moveTo(gridX, y);
    ctx.lineTo(gridX + grid * cellPx, y);
    ctx.stroke();
  }

  // Axis numbers — every cell when the grid is small, otherwise only at the major
  // gridlines (plus the very first cell), to keep large cross-stitch charts readable.
  ctx.fillStyle = NUMBER_COLOR;
  const numFontPx = Math.max(10, Math.floor(cellPx * 0.5));
  ctx.font = `${numFontPx}px "Segoe UI",system-ui,sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const everyCell = grid <= 30;
  for (let i = 0; i < grid; i++) {
    const onMajor = (i + 1) % settings.majorLineEvery === 0;
    if (!everyCell && i !== 0 && !onMajor) continue;
    const label = String(i + 1);
    ctx.fillText(label, gridX + i * cellPx + cellPx / 2, gridY - 16);
    ctx.fillText(label, gridX - 18, gridY + i * cellPx + cellPx / 2);
  }

  // Legend.
  ctx.font = `16px "Segoe UI",system-ui,sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  for (let i = 0; i < palette.length; i++) {
    const col = Math.floor(i / layout.legendRows);
    const row = i % layout.legendRows;
    const x = MARGIN_LEFT + col * LEGEND_COL_W;
    const y = layout.legendY + row * (LEGEND_SWATCH + LEGEND_GAP);
    const [r, g, b] = palette[i];
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fillRect(x, y, LEGEND_SWATCH, LEGEND_SWATCH);
    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, LEGEND_SWATCH - 1, LEGEND_SWATCH - 1);

    if (settings.showSymbols) {
      ctx.fillStyle = contrastColor(r, g, b);
      ctx.font = `bold ${Math.floor(LEGEND_SWATCH * 0.55)}px "Segoe UI Symbol",sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(symbolFor(i), x + LEGEND_SWATCH / 2, y + LEGEND_SWATCH / 2 + 1);
    }

    ctx.fillStyle = NUMBER_COLOR;
    ctx.font = `15px "Segoe UI",system-ui,sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const label = `${i + 1}  ${fmtHex(palette[i])}  · ${result.counts[i]}`;
    ctx.fillText(label, x + LEGEND_SWATCH + 8, y + LEGEND_SWATCH / 2);
  }
}

/** Convenience: full pipeline for a single bitmap. */
export function processBitmap(
  bitmap: ImageBitmap,
  settings: TemplateSettings
): TemplateResult {
  const small = rasterizeToGrid(bitmap, settings.grid);
  return buildTemplate(small, settings.paletteSize, settings.mergeDistance);
}

/** Render to a canvas and return a PNG Blob. */
export async function templateToBlob(
  result: TemplateResult,
  settings: TemplateSettings,
  progress?: ReadonlySet<number>
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  renderTemplate(canvas, result, settings, progress);
  return await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("canvas.toBlob returned null"));
    }, "image/png");
  });
}
